/* Country Autocomplete — Portal-grade trilingual search with waving flags.
 *
 * Design goals:
 *  • Single, premium-feeling input. NO native <select>.
 *  • Trilingual matching (ar / en / ur) + curated aliases per language.
 *  • Popular destinations pinned to the top of the dropdown.
 *  • Unicode flag emoji with a subtle CSS "wave" animation on hover/focus.
 *  • A friendly "🌍 أخرى / Other / دیگر" fallback that lets the client type
 *    a free-text destination.
 *  • Zero runtime dependency on external APIs — the catalog is fetched
 *    ONCE from /api/lookups/countries and cached in localStorage.
 *  • Blocked countries never reach the client (server filters them out)
 *    AND we double-check via /validate on final pick.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Check, Globe, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "vta_countries_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Arabic character normaliser — strips diacritics & unifies common
// letter variants so users typing fast still match exactly.
function normaliseAr(s) {
  if (!s) return "";
  return s
    .replace(/[\u0610-\u061A\u064B-\u065F\u0670]/g, "") // diacritics
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .toLowerCase()
    .trim();
}

function normaliseLatin(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").trim();
}

// Build the per-country searchable haystack (lower-cased, normalised).
function buildHaystack(c) {
  const ar = [c.name_ar, ...(c.aliases_ar || [])].map(normaliseAr).join(" | ");
  const en = [c.name_en, ...(c.aliases_en || [])].map(normaliseLatin).join(" | ");
  const ur = [c.name_ur, ...(c.aliases_ur || [])].join(" | "); // urdu kept raw
  return { ar, en, ur, code: (c.code || "").toLowerCase() };
}

function matchScore(hay, q) {
  // Higher score = better. Exact-prefix match wins, then substring.
  // Returns 0 if no hit. Score components blended to keep ordering stable.
  if (!q) return 0;
  let best = 0;
  const probe = (field, weight) => {
    if (!field) return;
    if (field === q) best = Math.max(best, 100 * weight);
    else if (field.startsWith(q)) best = Math.max(best, 80 * weight);
    else if (field.includes(` | ${q}`) || field.includes(`${q} `)) best = Math.max(best, 60 * weight);
    else if (field.includes(q)) best = Math.max(best, 40 * weight);
  };
  probe(hay.code, 1.0);
  probe(hay.en, 0.9);
  probe(hay.ar, 0.95);
  probe(hay.ur, 0.85);
  return best;
}

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || !data) return null;
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}
function saveCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* ignore quota */ }
}


/**
 * Props:
 *   value         — { code: string|null, free_text: string|null }
 *   onChange(v)   — called with new value
 *   lang          — "ar" | "en" | "ur"
 *   theme         — VTA theme object (bg, surface, text, border, gold…)
 *   testIdPrefix  — optional, defaults to "country-ac"
 */
export default function CountryAutocomplete({ value, onChange, lang = "ar", theme, testIdPrefix = "country-ac" }) {
  const isAr = lang === "ar";
  const isUr = lang === "ur";
  const dir = isAr || isUr ? "rtl" : "ltr";

  const [countries, setCountries] = useState(() => loadCache() || []);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // Fetch once on mount; tolerate cache.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/lookups/countries`).then(({ data }) => {
      if (cancelled) return;
      setCountries(data || []);
      saveCache(data || []);
    }).catch(() => { /* keep cache */ });
    return () => { cancelled = true; };
  }, []);

  // Outside-click closes the dropdown.
  useEffect(() => {
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Pre-build the haystacks once for the whole catalogue.
  const indexed = useMemo(
    () => countries.map((c) => ({ c, h: buildHaystack(c) })),
    [countries]
  );

  // Filter + rank.
  const results = useMemo(() => {
    const q = isAr || isUr ? normaliseAr(query) : normaliseLatin(query);
    if (!q) {
      // Empty query: popular first, then the rest as already sorted by server.
      const pop = indexed.filter(({ c }) => c.is_popular);
      const rest = indexed.filter(({ c }) => !c.is_popular);
      return [...pop, ...rest].slice(0, 120).map(({ c }) => ({ c, score: c.is_popular ? 10 : 1 }));
    }
    const scored = [];
    for (const { c, h } of indexed) {
      const s = matchScore(h, q);
      if (s > 0) scored.push({ c, score: s + (c.is_popular ? 5 : 0) });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 50);
  }, [indexed, query, isAr, isUr]);

  // Display name in the active UI language.
  const nameOf = (c) => (isUr ? c.name_ur : isAr ? c.name_ar : c.name_en);

  // Currently selected country object (if any).
  const selected = useMemo(
    () => (value?.code ? countries.find((c) => c.code === value.code) : null),
    [value, countries]
  );

  // ───────── Handlers ─────────
  const choose = async (c) => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/lookups/countries/validate/${c.code}`);
      if (!data?.ok) {
        setError(isAr ? "هذه الوجهة غير متاحة عبر البوابة حالياً." : isUr ? "یہ منزل اس وقت پورٹل کے ذریعے دستیاب نہیں ہے۔" : "This destination is currently unavailable via the portal.");
        return;
      }
    } catch { /* fall through and trust the public list */ }
    onChange({ code: c.code, free_text: null, country: c });
    setOpen(false);
    setQuery("");
  };

  const chooseOther = () => {
    onChange({ code: null, free_text: value?.free_text || "", country: null });
    setOpen(false);
    setQuery("");
  };

  const clear = () => {
    onChange({ code: null, free_text: null, country: null });
    setQuery("");
    setError("");
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx < results.length) choose(results[activeIdx].c);
      else chooseOther();
    } else if (e.key === "Escape") setOpen(false);
  };

  const labels = {
    ar: { placeholder: "اكتب اسم الدولة... (مثل: امريكا، كندا، فرنسا)", other: "أخرى", other_hint: "اكتب الدولة المطلوبة", popular: "الأكثر طلباً", all: "بقية الدول", empty: "لا توجد نتائج — جرّب اسماً مختلفاً", clear: "مسح" },
    en: { placeholder: "Type a country name… (e.g. USA, Canada, France)", other: "Other", other_hint: "Type your destination", popular: "Most requested", all: "All countries", empty: "No results — try a different name", clear: "Clear" },
    ur: { placeholder: "ملک کا نام لکھیں... (مثال: امریکہ، کینیڈا)", other: "دیگر", other_hint: "اپنی منزل لکھیں", popular: "سب سے زیادہ مطلوب", all: "تمام ممالک", empty: "کوئی نتیجہ نہیں — مختلف نام آزمائیں", clear: "صاف کریں" },
  };
  const L = labels[lang] || labels.ar;

  // ───────── Render ─────────
  return (
    <div ref={wrapRef} dir={dir} style={styles.wrap} data-testid={testIdPrefix}>
      {/* Field face — shows either the picked country or the search input */}
      {selected && !open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTimeout(() => wrapRef.current?.querySelector("input")?.focus(), 0); }}
          style={{ ...styles.face(theme), padding: "10px 12px" }}
          data-testid={`${testIdPrefix}-face-selected`}
        >
          <span style={styles.flagBig} className="vta-flag vta-flag-wave">{selected.flag}</span>
          <span style={{ flex: 1, textAlign: "start", fontWeight: 700, fontSize: 15 }}>
            {nameOf(selected)}
          </span>
          <span style={{ opacity: 0.5, fontSize: 11, fontFamily: "monospace" }}>{selected.code}</span>
          <span onClick={(e) => { e.stopPropagation(); clear(); }} style={styles.clearBtn} data-testid={`${testIdPrefix}-clear`}>
            <X size={14} />
          </span>
        </button>
      ) : value?.code === null && value?.free_text !== null && !open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTimeout(() => wrapRef.current?.querySelector("input")?.focus(), 0); }}
          style={{ ...styles.face(theme), padding: "10px 12px" }}
          data-testid={`${testIdPrefix}-face-other`}
        >
          <span style={styles.globe} className="vta-globe-spin"><Globe size={20} /></span>
          <span style={{ flex: 1, textAlign: "start", fontWeight: 700, fontSize: 15 }}>
            {value.free_text || L.other_hint}
          </span>
          <span onClick={(e) => { e.stopPropagation(); clear(); }} style={styles.clearBtn} data-testid={`${testIdPrefix}-clear`}>
            <X size={14} />
          </span>
        </button>
      ) : (
        <div style={styles.face(theme)}>
          <Search size={16} style={{ opacity: 0.5, margin: "0 8px" }} />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder={L.placeholder}
            style={styles.input(theme)}
            data-testid={`${testIdPrefix}-input`}
            autoComplete="off"
          />
        </div>
      )}

      {error && (
        <div style={styles.errorBox(theme)} data-testid={`${testIdPrefix}-error`}>{error}</div>
      )}

      {/* Free-text editor — visible only after the user explicitly chose "Other" */}
      {open === false && value?.code === null && value?.free_text !== null && (
        <input
          value={value.free_text || ""}
          onChange={(e) => onChange({ code: null, free_text: e.target.value })}
          placeholder={L.other_hint}
          style={{ ...styles.input(theme), marginTop: 8, border: `1.5px solid ${theme.border}`, borderRadius: 10, padding: "10px 12px" }}
          data-testid={`${testIdPrefix}-other-input`}
        />
      )}

      {/* Dropdown */}
      {open && (
        <div ref={listRef} style={styles.dropdown(theme)} data-testid={`${testIdPrefix}-dropdown`}>
          {results.length === 0 && !query && (
            <div style={styles.section(theme)}>{L.empty}</div>
          )}
          {results.length > 0 && (
            <>
              {results.map(({ c }, idx) => {
                const active = idx === activeIdx;
                const isPopular = c.is_popular;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => choose(c)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    style={styles.row(theme, active)}
                    data-testid={`${testIdPrefix}-row-${c.code}`}
                  >
                    <span style={styles.flagBig} className="vta-flag vta-flag-wave">{c.flag}</span>
                    <div style={{ flex: 1, minWidth: 0, textAlign: "start" }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: theme.text }}>
                        {nameOf(c)}
                        {isPopular && <span style={styles.popularDot}>★</span>}
                        {c.is_schengen && <span style={styles.schengenChip}>Schengen</span>}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2, fontFamily: "system-ui" }}>
                        {isAr ? `${c.name_en} · ${c.name_ur}` : isUr ? `${c.name_ar} · ${c.name_en}` : `${c.name_ar} · ${c.name_ur}`}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, opacity: 0.5, fontFamily: "monospace" }}>{c.code}</span>
                    {value?.code === c.code && <Check size={14} color="#16a34a" />}
                  </button>
                );
              })}
            </>
          )}
          {/* "Other" fallback — always last */}
          <button
            type="button"
            onClick={chooseOther}
            onMouseEnter={() => setActiveIdx(results.length)}
            style={{ ...styles.row(theme, activeIdx === results.length), borderTop: `1px dashed ${theme.borderSoft || theme.border}` }}
            data-testid={`${testIdPrefix}-row-other`}
          >
            <span style={styles.globe} className="vta-globe-spin"><Globe size={22} /></span>
            <span style={{ flex: 1, textAlign: "start", fontWeight: 800, color: theme.text }}>
              {L.other}
            </span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>أخرى / Other / دیگر</span>
          </button>
        </div>
      )}

      {/* Embedded CSS for the waving flag & spinning globe */}
      <style>{`
        @keyframes vta-wave {
          0%   { transform: rotate(-2deg) scale(1.0); }
          25%  { transform: rotate(3deg)  scale(1.05); }
          50%  { transform: rotate(-3deg) scale(1.05); }
          75%  { transform: rotate(2deg)  scale(1.05); }
          100% { transform: rotate(-2deg) scale(1.0); }
        }
        .vta-flag-wave {
          display: inline-block;
          transform-origin: 30% 70%;
          will-change: transform;
          animation: vta-wave 2.4s ease-in-out infinite;
        }
        .vta-flag-wave:hover, [data-testid$="-row-${"\""}}"]:hover .vta-flag-wave {
          animation-duration: 1.2s;
        }
        button:hover .vta-flag-wave, [data-active="true"] .vta-flag-wave {
          animation-duration: 1.0s;
        }
        @media (prefers-reduced-motion: reduce) {
          .vta-flag-wave { animation: none; }
        }
        @keyframes vta-spin {
          to { transform: rotate(360deg); }
        }
        .vta-globe-spin {
          display: inline-flex; align-items: center; justify-content: center;
        }
        button:hover .vta-globe-spin {
          animation: vta-spin 4s linear infinite;
        }
      `}</style>
    </div>
  );
}


// ───────────────────────── Styles ─────────────────────────

const styles = {
  wrap: { position: "relative", width: "100%" },

  face: (t) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    background: t.bg || "#fff",
    border: `1.5px solid ${t.border || "rgba(0,0,0,0.15)"}`,
    borderRadius: 12,
    cursor: "text",
    transition: "border-color 160ms, box-shadow 160ms",
    color: t.text || "#0c0c0c",
    fontFamily: "inherit",
  }),

  input: (t) => ({
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: t.text || "#0c0c0c",
    fontSize: 15,
    padding: "10px 4px",
    fontWeight: 600,
    fontFamily: "inherit",
  }),

  clearBtn: {
    width: 22, height: 22, display: "inline-flex", alignItems: "center",
    justifyContent: "center", borderRadius: 999, cursor: "pointer",
    background: "rgba(0,0,0,0.06)", color: "currentColor", marginInlineStart: 4,
  },

  dropdown: (t) => ({
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0, right: 0,
    zIndex: 1000,
    background: t.surface || "#fff",
    border: `1px solid ${t.border || "rgba(0,0,0,0.15)"}`,
    borderRadius: 14,
    boxShadow: "0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.04) inset",
    maxHeight: 380,
    overflowY: "auto",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    padding: 4,
  }),

  section: (t) => ({
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 800,
    color: t.gold || t.textMuted || "#888",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  }),

  row: (t, active) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 12px",
    background: active ? (t.surfaceHover || "rgba(212,175,55,0.10)") : "transparent",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    color: t.text || "#0c0c0c",
    textAlign: "start",
    fontFamily: "inherit",
    transition: "background 120ms",
  }),

  flagBig: { fontSize: 26, lineHeight: 1, flexShrink: 0, filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))" },
  globe: { fontSize: 22, color: "#16a34a", flexShrink: 0 },

  popularDot: {
    marginInlineStart: 6,
    fontSize: 11,
    color: "#d4af37",
    fontWeight: 900,
    textShadow: "0 0 6px rgba(212,175,55,0.6)",
  },

  schengenChip: {
    marginInlineStart: 6,
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: 999,
    background: "rgba(37,99,235,0.12)",
    color: "#2563eb",
    border: "1px solid rgba(37,99,235,0.3)",
  },

  errorBox: (t) => ({
    marginTop: 6,
    padding: "8px 10px",
    background: "rgba(220,38,38,0.10)",
    color: "#dc2626",
    border: "1px solid rgba(220,38,38,0.45)",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
  }),
};
